import { useEffect, useState } from "react";
import api, { apiErrorMsg, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings, Users, Percent, Plus, Power, Award, MapPin, DollarSign } from "lucide-react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function CelAdmin() {
  const [config, setConfig] = useState(null);
  const [pctInput, setPctInput] = useState("");
  const [pctVendInput, setPctVendInput] = useState("");
  const [templateInput, setTemplateInput] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);

  const [indicadores, setIndicadores] = useState([]);
  const [openNovo, setOpenNovo] = useState(false);
  const [novo, setNovo] = useState({ nome: "", email: "", password: "", telefone: "", observacoes: "" });
  const [busyNovo, setBusyNovo] = useState(false);

  const [vendedoras, setVendedoras] = useState([]);
  const [editVend, setEditVend] = useState(null); // {id, nome, ufs_atuacao}
  const [vendUfs, setVendUfs] = useState([]);
  const [busyVend, setBusyVend] = useState(false);

  const [editCom, setEditCom] = useState(null); // indicador para editar % de comissão
  const [comInput, setComInput] = useState("");
  const [busyCom, setBusyCom] = useState(false);

  const [editMeta, setEditMeta] = useState(null); // vendedora CeL para editar meta mensal
  const [metaInput, setMetaInput] = useState("");
  const [busyMeta, setBusyMeta] = useState(false);

  const loadConfig = () => {
    api.get("/cel/config").then((r) => {
      setConfig(r.data);
      setPctInput(String(r.data.comissao_pct_padrao ?? ""));
      setPctVendInput(String(r.data.comissao_vendedora_pct_padrao ?? ""));
      setTemplateInput(r.data.proposta_template || "");
    });
  };
  const loadIndicadores = () => {
    api.get("/cel/indicadores").then((r) => setIndicadores(r.data || []));
  };
  const loadVendedoras = () => {
    api.get("/cel/vendedoras").then((r) => setVendedoras(r.data || []));
  };

  useEffect(() => { loadConfig(); loadIndicadores(); loadVendedoras(); }, []);

  const salvarConfig = async () => {
    const pct = Number(pctInput);
    const pctVend = Number(pctVendInput);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("% indicador inválido (0-100)."); return; }
    if (isNaN(pctVend) || pctVend < 0 || pctVend > 100) { toast.error("% vendedora inválido (0-100)."); return; }
    setSavingCfg(true);
    try {
      await api.patch("/cel/config", {
        comissao_pct_padrao: pct,
        comissao_vendedora_pct_padrao: pctVend,
        proposta_template: templateInput || null,
      });
      toast.success("Configuração salva.");
      loadConfig();
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally { setSavingCfg(false); }
  };

  const abrirEditVend = (v) => {
    setEditVend(v);
    setVendUfs(v.ufs_atuacao || []);
  };
  const toggleUf = (uf) => {
    setVendUfs((cur) => cur.includes(uf) ? cur.filter((x) => x !== uf) : [...cur, uf]);
  };
  const salvarUfs = async () => {
    if (!editVend) return;
    setBusyVend(true);
    try {
      await api.patch(`/cel/vendedoras/${editVend.id}/ufs`, { ufs_atuacao: vendUfs });
      toast.success(`UFs de ${editVend.nome} atualizadas.`);
      setEditVend(null);
      loadVendedoras();
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally { setBusyVend(false); }
  };

  const abrirEditCom = (u) => {
    setEditCom(u);
    setComInput(u.comissao_indicador_pct != null ? String(u.comissao_indicador_pct) : "");
  };
  const salvarComissao = async () => {
    if (!editCom) return;
    const raw = comInput.trim();
    const pct = raw === "" ? null : Number(raw);
    if (raw !== "" && (isNaN(pct) || pct < 0 || pct > 100)) {
      toast.error("Informe uma % entre 0 e 100, ou deixe vazio para usar o padrão.");
      return;
    }
    setBusyCom(true);
    try {
      await api.patch(`/cel/indicadores/${editCom.id}/comissao`, { comissao_pct: pct });
      toast.success(`Comissão de ${editCom.nome} atualizada.`);
      setEditCom(null);
      loadIndicadores();
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally { setBusyCom(false); }
  };

  const abrirEditMeta = (v) => {
    setEditMeta(v);
    setMetaInput(v.meta_receita_mes != null && Number(v.meta_receita_mes) > 0 ? String(v.meta_receita_mes) : "");
  };
  const salvarMeta = async () => {
    if (!editMeta) return;
    const val = Number(metaInput || 0);
    if (isNaN(val) || val < 0) {
      toast.error("Informe uma meta válida (R$).");
      return;
    }
    setBusyMeta(true);
    try {
      await api.patch(`/cel/vendedoras/${editMeta.id}/meta`, { meta_receita: val });
      toast.success(`Meta de ${editMeta.nome} atualizada.`);
      setEditMeta(null);
      loadVendedoras();
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally { setBusyMeta(false); }
  };

  const criarIndicador = async () => {
    if (!novo.nome || !novo.email || !novo.password) { toast.error("Preencha nome, e-mail e senha."); return; }
    setBusyNovo(true);
    try {
      await api.post("/cel/indicadores", novo);
      toast.success("Indicador criado.");
      setOpenNovo(false);
      setNovo({ nome: "", email: "", password: "", telefone: "", observacoes: "" });
      loadIndicadores();
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally { setBusyNovo(false); }
  };

  const toggleAtivo = async (u) => {
    const url = u.indicador_ativo ? `/cel/indicadores/${u.id}/desativar` : `/cel/indicadores/${u.id}/ativar`;
    try {
      await api.post(url);
      toast.success(u.indicador_ativo ? "Indicador desativado." : "Indicador ativado.");
      loadIndicadores();
    } catch (err) {
      toast.error(apiErrorMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="cel-admin-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <Settings className="w-7 h-7 text-sky-600" /> Admin CeL Consultoria
        </h1>
        <p className="text-sm text-slate-500 mt-1">Configurações do sub-CRM CeL e cadastro de indicadores externos.</p>
      </div>

      {/* Configuração de comissão */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Percent className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-bold text-slate-900">Comissões padrão</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>% do indicador (0-100)</Label>
            <Input type="number" step="0.5" min="0" max="100" value={pctInput} onChange={(e) => setPctInput(e.target.value)} data-testid="cfg-comissao-pct" />
            <div className="text-[11px] text-slate-500 mt-1">Comissão paga a quem indicou (vendedora de outro núcleo, diretor, indicador externo).</div>
          </div>
          <div>
            <Label>% da vendedora CeL responsável (0-100)</Label>
            <Input type="number" step="0.5" min="0" max="100" value={pctVendInput} onChange={(e) => setPctVendInput(e.target.value)} data-testid="cfg-comissao-vendedora-pct" />
            <div className="text-[11px] text-slate-500 mt-1">Comissão paga à vendedora que fecha o contrato. Se a vendedora também é o indicador (auto-indicação), esta não é somada.</div>
          </div>
        </div>
        <div className="mt-4">
          <Label>Template HTML da proposta (opcional)</Label>
          <Textarea rows={5} value={templateInput} onChange={(e) => setTemplateInput(e.target.value)} placeholder="Deixe vazio para usar o template padrão. Placeholders: {municipio_nome}, {uf}, {tipo_orgao}, {cnpj}, {contato_nome}, {contato_cargo}, {valor_total}, {prazo}, {escopo}, {condicoes_pagamento}, {data_hoje}, {indicador_nome}" data-testid="cfg-template" />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={salvarConfig} disabled={savingCfg} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="cfg-save">
            {savingCfg ? "Salvando..." : "Salvar configuração"}
          </Button>
          {config && (
            <span className="text-[11px] text-slate-500">Atualizado em {formatDateTime(config.atualizado_em)}</span>
          )}
        </div>
      </div>

      {/* Vendedoras CeL · UFs de atuação */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-sky-600" />
          <h2 className="text-lg font-bold text-slate-900">Vendedoras CeL · UFs de atuação</h2>
        </div>
        {vendedoras.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">Nenhuma vendedora CeL cadastrada. Cadastre em Admin → Usuários com papel "vendedora" e núcleo "CeL Consultoria".</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wider border-b border-slate-200">
                <th className="py-2 pr-2">Vendedora</th>
                <th className="py-2 pr-2">UFs de atuação</th>
                <th className="py-2 pr-2 text-right">Meta mensal</th>
                <th className="py-2 pr-2 text-center">Ativas</th>
                <th className="py-2 pr-2 text-center">Fechadas</th>
                <th className="py-2 pr-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vendedoras.map((v) => (
                <tr key={v.id} data-testid={`row-vend-${v.id}`}>
                  <td className="py-2 pr-2">
                    <div className="font-semibold text-slate-800">{v.nome}</div>
                    <div className="text-[11px] text-slate-500">{v.email}</div>
                  </td>
                  <td className="py-2 pr-2">
                    {(v.ufs_atuacao || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {v.ufs_atuacao.map((uf) => (
                          <span key={uf} className="inline-block px-2 py-0.5 rounded bg-sky-100 text-sky-800 text-[11px] font-bold font-mono">{uf}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[11px] text-amber-700 italic">Nenhuma — vendedora não verá a carteira</span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right font-mono text-sky-800 font-bold" data-testid={`meta-cell-${v.id}`}>
                    {v.meta_receita_mes && Number(v.meta_receita_mes) > 0
                      ? `R$ ${Number(v.meta_receita_mes).toLocaleString('pt-BR')}`
                      : <span className="text-slate-400 italic font-sans font-normal">—</span>}
                  </td>
                  <td className="py-2 pr-2 text-center font-bold">{v.qtd_ativas}</td>
                  <td className="py-2 pr-2 text-center font-bold text-emerald-700">{v.qtd_fechadas}</td>
                  <td className="py-2 pr-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => abrirEditMeta(v)} className="mr-1" data-testid={`btn-edit-meta-${v.id}`}>
                      <DollarSign className="w-3.5 h-3.5 mr-1" /> Meta
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => abrirEditVend(v)} data-testid={`btn-edit-ufs-${v.id}`}>
                      <MapPin className="w-3.5 h-3.5 mr-1" /> UFs
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Indicadores externos */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-sky-600" />
            <h2 className="text-lg font-bold text-slate-900">Indicadores externos</h2>
          </div>
          <Button onClick={() => setOpenNovo(true)} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="btn-novo-indicador">
            <Plus className="w-4 h-4 mr-1" /> Novo indicador
          </Button>
        </div>
        {indicadores.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">Nenhum indicador cadastrado ainda.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wider border-b border-slate-200">
                <th className="py-2 pr-2">Nome</th>
                <th className="py-2 pr-2">E-mail</th>
                <th className="py-2 pr-2">Telefone</th>
                <th className="py-2 pr-2 text-center">% Comissão</th>
                <th className="py-2 pr-2 text-center">Indicações</th>
                <th className="py-2 pr-2 text-center">Fechadas</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {indicadores.map((u) => (
                <tr key={u.id} data-testid={`row-indic-${u.id}`}>
                  <td className="py-2 pr-2 font-semibold text-slate-800">{u.nome}</td>
                  <td className="py-2 pr-2 text-slate-600">{u.email}</td>
                  <td className="py-2 pr-2 text-slate-600">{u.telefone || "—"}</td>
                  <td className="py-2 pr-2 text-center" data-testid={`com-cell-${u.id}`}>
                    {u.comissao_indicador_pct != null ? (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold font-mono">{u.comissao_indicador_pct}%</span>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Padrão</span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-center">{u.qtd_indicacoes}</td>
                  <td className="py-2 pr-2 text-center font-bold text-emerald-700">{u.qtd_fechadas}</td>
                  <td className="py-2 pr-2">
                    {u.indicador_ativo ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">Ativo</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-bold">Desativado</span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => abrirEditCom(u)} className="mr-1" data-testid={`edit-com-${u.id}`}>
                      <Percent className="w-3.5 h-3.5 mr-1" /> % Comissão
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleAtivo(u)} data-testid={`toggle-${u.id}`}>
                      <Power className="w-3.5 h-3.5 mr-1" /> {u.indicador_ativo ? "Desativar" : "Ativar"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal novo indicador */}
      <Dialog open={openNovo} onOpenChange={setOpenNovo}>
        <DialogContent data-testid="modal-novo-indicador">
          <DialogHeader>
            <DialogTitle>Novo indicador externo</DialogTitle>
            <DialogDescription>Ele terá acesso apenas ao próprio painel de indicações (somente leitura).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} data-testid="novo-indic-nome" />
            </div>
            <div>
              <Label>E-mail *</Label>
              <Input type="email" value={novo.email} onChange={(e) => setNovo({ ...novo, email: e.target.value })} data-testid="novo-indic-email" />
            </div>
            <div>
              <Label>Senha inicial *</Label>
              <Input type="text" value={novo.password} onChange={(e) => setNovo({ ...novo, password: e.target.value })} placeholder="Mín. 4 caracteres" data-testid="novo-indic-password" />
              <div className="text-[11px] text-slate-500 mt-0.5">Compartilhe com o indicador. Ele pode trocar depois em seu perfil.</div>
            </div>
            <div>
              <Label>Telefone</Label>
              <Input value={novo.telefone} onChange={(e) => setNovo({ ...novo, telefone: e.target.value })} />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea rows={2} value={novo.observacoes} onChange={(e) => setNovo({ ...novo, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNovo(false)}>Cancelar</Button>
            <Button onClick={criarIndicador} disabled={busyNovo} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="novo-indic-submit">
              {busyNovo ? "Criando..." : "Criar indicador"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal editar meta mensal da vendedora CeL */}
      <Dialog open={!!editMeta} onOpenChange={(v) => !v && setEditMeta(null)}>
        <DialogContent data-testid="modal-editar-meta">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-sky-600" /> Meta mensal · {editMeta?.nome}
            </DialogTitle>
            <DialogDescription>
              Meta de receita fechada por mês (R$). O gap aparece direto no Dashboard dela, com barra de progresso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Meta de receita mensal (R$)</Label>
              <Input
                type="number"
                step="1000"
                min="0"
                value={metaInput}
                onChange={(e) => setMetaInput(e.target.value)}
                placeholder="Ex.: 80000"
                data-testid="edit-meta-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Deixe 0 (ou vazio) para desligar a meta. Só conta valor de contratos <b>assinados no mês corrente</b>.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMeta(null)} disabled={busyMeta}>Cancelar</Button>
            <Button onClick={salvarMeta} disabled={busyMeta} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="salvar-meta-submit">
              {busyMeta ? "Salvando..." : "Salvar meta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal editar % de comissão do indicador */}
      <Dialog open={!!editCom} onOpenChange={(v) => !v && setEditCom(null)}>
        <DialogContent data-testid="modal-editar-comissao">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-emerald-600" /> % Comissão · {editCom?.nome}
            </DialogTitle>
            <DialogDescription>
              Define a % de comissão específica deste indicador. Deixe vazio para usar o padrão global da configuração.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>% de comissão (0-100)</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={comInput}
                onChange={(e) => setComInput(e.target.value)}
                placeholder="Ex.: 10 · deixe vazio para usar padrão global"
                data-testid="edit-com-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Ex.: parceiros externos costumam receber 10%; equipe interna, 2%.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCom(null)} disabled={busyCom}>Cancelar</Button>
            <Button onClick={salvarComissao} disabled={busyCom} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="salvar-com-submit">
              {busyCom ? "Salvando..." : "Salvar % de comissão"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal editar UFs da vendedora */}
      <Dialog open={!!editVend} onOpenChange={(v) => !v && setEditVend(null)}>
        <DialogContent className="max-w-2xl" data-testid="modal-editar-ufs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-sky-600" /> UFs de atuação · {editVend?.nome}
            </DialogTitle>
            <DialogDescription>
              A vendedora só verá municípios das UFs marcadas na Carteira CeL. Selecione uma ou mais.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-6 md:grid-cols-9 gap-2 py-2">
            {UFS.map((uf) => {
              const selected = vendUfs.includes(uf);
              return (
                <button
                  key={uf}
                  type="button"
                  onClick={() => toggleUf(uf)}
                  className={`px-2 py-1.5 rounded font-mono font-bold text-sm border transition ${selected ? "bg-sky-600 text-white border-sky-700 shadow-sm" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                  data-testid={`uf-toggle-${uf}`}
                >
                  {uf}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-slate-500">{vendUfs.length} UF{vendUfs.length === 1 ? "" : "s"} selecionada{vendUfs.length === 1 ? "" : "s"}: <b>{vendUfs.join(" · ")}</b></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVend(null)} disabled={busyVend}>Cancelar</Button>
            <Button onClick={salvarUfs} disabled={busyVend} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="salvar-ufs-submit">
              {busyVend ? "Salvando..." : "Salvar UFs"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
